/**
 * Note search: a `parseSearchQuery` result compiled to one SQL statement over
 * the active notes (not trashed, not archived), pinned first, then most recent.
 *
 * Text terms match as a substring of the title or content (`LIKE`), or the way
 * the native FTS5 table does (token prefix, diacritics folded: `cafe` finds
 * `café`) through `caliu_fts`, a function `registerSearchFunctions` adds. Tags
 * resolve against the tag tree (`#work` includes `#work/x`); specials are plain
 * predicates. Mirrors Swift `NoteSearch` and Kotlin `NoteSearch`; all three run
 * `packages/utils/src/searchScenarios.fixtures.json`.
 *
 * `source: "shared"` runs the same query over the shared-with-me cache
 * (`shared_notes`), which holds a title, content and dates only: text matches
 * by `LIKE` alone (no FTS table there on native), the content specials as for
 * notes, the account's own data (tags, pin, read-only) is absent, and
 * attachments come from `sharedAttachments`.
 */
import { normalizeHashtagBody, type ParsedQuery, type SearchTerm, type SpecialTerm } from "@caliu/utils"
import { projectNoteAttachments } from "./attachmentProjection"
import { tagFullPath } from "./tagHierarchy"

export interface SearchTag {
  id: string
  name: string
  parentId?: string | null
}

export interface SearchSqlContext {
  tags: SearchTag[]
  now: Date
  source?: "notes" | "shared"
  /** For `source: "shared"`: the cached shared notes with an image / a file attachment. */
  sharedAttachments?: { image: string[]; file: string[] }
}

type Clause = { sql: string; params: unknown[] }

const OPEN_TASK = "(notes.content LIKE '%- [ ]%' OR notes.content LIKE '%* [ ]%' OR notes.content LIKE '%+ [ ]%')"
// LIKE is case-insensitive for ASCII, so `[x]` also finds `[X]`.
const DONE_TASK = "(notes.content LIKE '%- [x]%' OR notes.content LIKE '%* [x]%' OR notes.content LIKE '%+ [x]%')"
const hasKind = (kind: "image" | "file") =>
  `EXISTS (SELECT 1 FROM attachment_projection ap WHERE ap.note_id = notes.id AND ap.kind = '${kind}')`

/** `null` when the query has no terms. */
export function compileSearchSql(parsed: ParsedQuery, ctx: SearchSqlContext, limit: number): Clause | null {
  if (parsed.groups.length === 0) return null
  const tagIds = tagResolver(ctx.tags)
  const params: unknown[] = []
  const groups = parsed.groups.map((group) =>
    "(" + group.terms.map((term) => {
      const clause = termClause(term, group.titleOnly, tagIds, ctx)
      params.push(...clause.params)
      return term.negated ? `NOT ${clause.sql}` : clause.sql
    }).join(" AND ") + ")",
  )
  params.push(limit)
  if (ctx.source === "shared") {
    return {
      sql: `SELECT notes.id FROM shared_note_refs snr JOIN shared_notes notes ON notes.id = snr.note_id
        WHERE (${groups.join(" OR ")})
        ORDER BY notes.updated_at DESC
        LIMIT ?`,
      params,
    }
  }
  return {
    sql: `SELECT notes.id FROM notes
      WHERE notes.deleted_at IS NULL AND notes.archived_at IS NULL
        AND (${groups.join(" OR ")})
      ORDER BY notes.is_pinned DESC, notes.updated_at DESC
      LIMIT ?`,
    params,
  }
}

type Rows = (sql: string, params: unknown[]) => unknown[][]

/**
 * The shared-with-me notes the query matches, most recent first. Reads the
 * cached contents for their attachments only when the query asks about them.
 */
export function searchSharedNoteIds(parsed: ParsedQuery, now: Date, limit: number, rows: Rows): string[] {
  const asksAttachments = parsed.groups.some((g) =>
    g.terms.some((t) => t.kind === "special" && (t.name === "images" || t.name === "files" || t.name === "attachments")),
  )
  const sharedAttachments = { image: [] as string[], file: [] as string[] }
  if (asksAttachments) {
    for (const [id, content] of rows("SELECT id, content FROM shared_notes", []) as Array<[string, string]>) {
      const kinds = new Set(projectNoteAttachments({ id, userId: "", content, createdAt: "", updatedAt: "" }).map((a) => a.kind))
      for (const kind of kinds) sharedAttachments[kind].push(id)
    }
  }
  const compiled = compileSearchSql(parsed, { tags: [], now, source: "shared", sharedAttachments }, limit)
  return compiled ? rows(compiled.sql, compiled.params).map((row) => row[0] as string) : []
}

/**
 * Lowercase the way FTS5's unicode61 tokenizer (`remove_diacritics=1`, the
 * native default) does: a Latin letter with one diacritic loses it (`é` → `e`),
 * one with two keeps them (`ệ`), and a separately typed combining mark is
 * dropped. Measured against SQLite: it agrees on all but two code points.
 */
function ftsFold(s: string): string {
  let out = ""
  for (const ch of s) {
    const nfd = ch.normalize("NFD")
    const one = nfd.length === 2 && /^[A-Za-z]\p{M}$/u.test(nfd)
    out += (one ? nfd[0] : ch).toLowerCase()
  }
  return out.replace(/\p{M}/gu, "")
}
const ftsTokens = (s: string) => ftsFold(s).split(/[^\p{L}\p{N}\p{Co}]+/u).filter(Boolean)

/**
 * Whether `text` matches the FTS5 query `"term"*` (`prefix`) or `"term"` the
 * way the native `notes_fts` table (unicode61, see `ftsFold`) does: the
 * term's tokens appear consecutively, the last one as a prefix when `prefix`.
 */
export function ftsMatches(text: string | null, term: string, prefix: boolean): boolean {
  const want = ftsTokens(term)
  if (!text || want.length === 0) return false
  const have = ftsTokens(text)
  const last = want.length - 1
  outer: for (let i = 0; i + want.length <= have.length; i++) {
    for (let j = 0; j <= last; j++) {
      const ok = j === last && prefix ? have[i + j].startsWith(want[j]) : have[i + j] === want[j]
      if (!ok) continue outer
    }
    return true
  }
  return false
}

/**
 * Adds `caliu_fts(text, term, prefix)` (see `ftsMatches`) to a sql.js database.
 * ponytail: tokenizes each note per query (SQLite only calls it when the LIKE
 * missed); a folded FTS table would index it once if large accounts feel slow.
 */
export function registerSearchFunctions(db: { create_function(name: string, fn: (...args: never[]) => unknown): unknown }): void {
  db.create_function("caliu_fts", ((text: string | null, term: string, prefix: number) =>
    ftsMatches(text, term, prefix === 1) ? 1 : 0) as (...args: never[]) => unknown)
}

/** The tag ids a handle names, plus every descendant when `nested`. */
function tagResolver(rows: SearchTag[]): (handle: string, nested: boolean) => string[] {
  const tags = rows.map((t) => ({ id: t.id, name: t.name, parentId: t.parentId ?? undefined }))
  const byId = new Map(tags.map((t) => [t.id, t]))
  const byPath = new Map<string, string[]>()
  const children = new Map<string, string[]>()
  for (const tag of tags) {
    const path = normalizeHashtagBody(tagFullPath(tag, byId))
    byPath.set(path, [...(byPath.get(path) ?? []), tag.id])
    if (tag.parentId) children.set(tag.parentId, [...(children.get(tag.parentId) ?? []), tag.id])
  }
  return (handle, nested) => {
    const ids = [...(byPath.get(handle) ?? [])]
    if (!nested) return ids
    const seen = new Set(ids)
    for (let i = 0; i < ids.length; i++) {
      for (const child of children.get(ids[i]) ?? []) {
        if (!seen.has(child)) { seen.add(child); ids.push(child) }
      }
    }
    return ids
  }
}

function escapeLike(text: string): string {
  return text.replace(/[\\%_]/g, (c) => `\\${c}`)
}

function termClause(
  term: SearchTerm,
  titleOnly: boolean,
  tagIds: (handle: string, nested: boolean) => string[],
  ctx: SearchSqlContext,
): Clause {
  switch (term.kind) {
    case "text": {
      const like = `%${escapeLike(term.text)}%`
      const prefix = term.phrase ? 0 : 1
      const columns = titleOnly ? ["notes.title"] : ["notes.title", "notes.content"]
      const parts = columns.map((c) => `${c} LIKE ? ESCAPE '\\'`)
      const params: unknown[] = columns.map(() => like)
      // The shared cache has no FTS table on native, so no folding there either.
      for (const c of ctx.source === "shared" ? [] : columns) {
        parts.push(`caliu_fts(${c}, ?, ?)`)
        params.push(term.text, prefix)
      }
      return { sql: `(${parts.join(" OR ")})`, params }
    }
    case "tag": {
      if (ctx.source === "shared") return { sql: "0", params: [] }
      const ids = tagIds(term.handle, term.nested)
      if (ids.length === 0) return { sql: "0", params: [] }
      return {
        sql: `notes.id IN (SELECT note_id FROM note_tags WHERE tag_id IN (${ids.map(() => "?").join(", ")}))`,
        params: ids,
      }
    }
    case "special":
      return ctx.source === "shared" ? sharedSpecialClause(term, ctx) : specialClause(term, ctx.now)
  }
}

/** A shared-with-me note has none of the account's own data, and no projection. */
function sharedSpecialClause(term: SpecialTerm, ctx: SearchSqlContext): Clause {
  const inIds = (ids: string[]): Clause =>
    ids.length === 0 ? { sql: "0", params: [] } : { sql: `notes.id IN (${ids.map(() => "?").join(", ")})`, params: ids }
  const { image = [], file = [] } = ctx.sharedAttachments ?? {}
  switch (term.name) {
    case "tagged":
    case "pinned":
    case "readonly":
      return { sql: "0", params: [] }
    case "untagged": return { sql: "1", params: [] }
    case "images": return inIds(image)
    case "files": return inIds(file)
    case "attachments": return inIds([...new Set([...image, ...file])])
    default: return specialClause(term, ctx.now)
  }
}

function specialClause(term: SpecialTerm, now: Date): Clause {
  const plain = (sql: string): Clause => ({ sql, params: [] })
  switch (term.name) {
    case "tagged": return plain("EXISTS (SELECT 1 FROM note_tags nt WHERE nt.note_id = notes.id)")
    case "untagged": return plain("NOT EXISTS (SELECT 1 FROM note_tags nt WHERE nt.note_id = notes.id)")
    case "pinned": return plain("notes.is_pinned = 1")
    case "readonly": return plain("notes.is_read_only = 1")
    case "untitled": return plain("trim(notes.title) = ''")
    case "task": return plain(`(${OPEN_TASK} OR ${DONE_TASK})`)
    case "todo": return plain(OPEN_TASK)
    case "done": return plain(`(${DONE_TASK} AND NOT ${OPEN_TASK})`)
    case "images": return plain(hasKind("image"))
    case "files": return plain(hasKind("file"))
    case "attachments": return plain("EXISTS (SELECT 1 FROM attachment_projection ap WHERE ap.note_id = notes.id)")
    case "code": return plain("(notes.content LIKE '%`%' OR notes.content LIKE '%~~~%')")
    case "wikilinks": return plain("notes.content LIKE '%[[%]]%'")
    case "modified":
    case "created":
      return dateClause(term, term.name === "created" ? "notes.created_at" : "notes.updated_at", now)
  }
}

/** Local-day boundaries compared as the stored ISO-8601 UTC strings, as the list's Today filter does. */
function dateClause(term: SpecialTerm, column: string, now: Date): Clause {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const day = (offset: number, from: Date) => new Date(from.getFullYear(), from.getMonth(), from.getDate() + offset)
  const iso = (d: Date) => d.toISOString()
  if (term.days !== undefined) return { sql: `${column} >= ?`, params: [iso(day(-(term.days - 1), today))] }
  if (term.yesterday) return { sql: `(${column} >= ? AND ${column} < ?)`, params: [iso(day(-1, today)), iso(today)] }
  if (!term.date) return { sql: "0", params: [] }
  const { cmp, y, m, d } = term.date
  // setFullYear, not the constructor, which reads years 0-99 as 1900-1999.
  const local = (year: number, month: number, day: number) => {
    const date = new Date(2000, 0, 1)
    date.setFullYear(year, month, day)
    return date
  }
  const start = local(y, (m ?? 1) - 1, d ?? 1)
  const end = d !== undefined ? local(y, m! - 1, d + 1) : m !== undefined ? local(y, m, 1) : local(y + 1, 0, 1)
  if (cmp === "on") return { sql: `(${column} >= ? AND ${column} < ?)`, params: [iso(start), iso(end)] }
  if (cmp === "before") return { sql: `${column} < ?`, params: [iso(start)] }
  return { sql: `${column} >= ?`, params: [iso(end)] }
}
